<template>
  <div>
    <loading-panel :loading="loading"></loading-panel>

    <!-- Shared filter bar (all viewports): a single instance owns saved views.
         Two instances with the same saved-filters-key would each run independent
         saved-views logic and fight over autosave, so it lives here once. -->
    <div class="row mt-3">
      <div class="col-12">
        <card>
          <h4 slot="header" class="card-title">Checklist history</h4>
          <filter-bar
            :filters="filterSchema"
            :initial-value="filterValues"
            :result-count="totalRows"
            :loading="refreshing"
            :per-page="perPage"
            :per-page-options="perPageOptions"
            saved-filters-key="checklist-history"
            @change="onFiltersChanged"
            @update:perPage="onPerPageChange"
          >
            <template #actions>
              <base-button v-if="hasPermissions('download_data') && totalRows > 0" icon class="btn-link text-white" @click="downloadCSV()">
                <i class="tim-icons icon-cloud-download-93"></i>
              </base-button>
            </template>
          </filter-bar>

          <!--Medium and large screens-->
          <div class="mt-3 d-none d-sm-block overflow-scroll">
          <div class="mt-3" v-if="noDataAvailable === true">
            <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <h4 class="mt-3">No resets have been completed yet.</h4>
            </div>
          </div>
          <div v-if="noDataAvailable === false" class="row table-content">
            <div v-if="load" class="col-md-12 col-lg-12 col-sm-12 col-12">
              <b-table hover :items="completedResets"
                        :current-page="currentPage"
                        :fields="fields">
                <template v-slot:cell(checklist)="row">
                  <router-link :to="'/checklists/history/instance?id=' + row.item.id" class="checklist-event-link">
                    {{row.item.checklist_name}}
                    <i class="tim-icons icon-minimal-right ml-1" style="font-size: 10px"></i>
                  </router-link>
                  <div class="d-flex align-items-center flex-wrap mt-1" style="gap: 6px">
                    <span class="cr-pill" :class="statusClass(row.item.status)">{{ statusLabel(row.item.status) }}</span>
                    <span class="cr-pill cr-pill--muted">{{ row.item.steps_completed }} / {{ row.item.steps_total }} steps</span>
                    <span v-if="row.item.issues_count > 0" class="cr-pill cr-pill--danger">{{ row.item.issues_count }} issue{{ row.item.issues_count > 1 ? 's' : '' }}</span>
                  </div>
                  <div v-if="row.item.tags && row.item.tags.length" class="tags-row mt-1">
                    <span v-for="tag in row.item.tags" :key="'HT' + row.item.id + '-' + tag.id" class="tag-chip text-white"
                          :class="{ 'tag-chip--archived': tag.is_archived }" :style="tagChipStyle(tag.color)">{{ tag.name }}</span>
                  </div>
                </template>
                <template v-slot:cell(game)="row">
                  <div v-if="row.item.game_id" class="ch-event">
                    <span class="cr-pill">{{ row.item.game_name }}</span>
                    <template v-if="row.item.game_instance_id">
                      <a :href="'/games/history/instance?id=' + row.item.game_instance_id" target="_blank" class="checklist-event-link ch-event__session">
                        {{ getDayOfWeek(row.item.booking_date) }}{{ getBookingDate(row.item.booking_date) }}, {{ getTime(row.item.start_time) }}
                        <i class="tim-icons icon-minimal-right ml-1" style="font-size: 10px"></i>
                      </a>
                      <span class="ch-event__host">Hosted by {{ row.item.gm_first_name }} {{ row.item.gm_last_name }}</span>
                    </template>
                  </div>
                  <span v-else>-</span>
                </template>
                <template v-slot:cell(started_at)="row">
                  <span v-if="!row.item.reset_start_time">-</span>
                  <span v-if="row.item.reset_start_time" >
                    {{getLocalDate(row.item.reset_start_time)}},
                    {{getLocalTime(row.item.reset_start_time)}}
                  </span>
                  <div class="ch-event__host">{{ row.item.r_first_name }} {{ row.item.r_last_name }}</div>
                </template>

                <template v-slot:cell(completed_at)="row">
                  <span v-if="row.item.status === 'in_progress'">-</span>
                  <template v-else>
                    <span>
                      {{getLocalDate(row.item.timestamp)}},  &nbsp;
                      {{getLocalTime(row.item.timestamp)}}
                    </span>
                    <div class="ch-event__host">{{ row.item.cb_first_name }} {{ row.item.cb_last_name }}</div>
                  </template>
                </template>
                <template v-slot:cell(completion_time)="row">
                  <span v-if="row.item.status === 'in_progress'">-</span>
                  <span v-else>{{getCompletionTime(row.item.reset_start_time, row.item.timestamp)}}</span>
                </template>
                <template v-slot:cell(game_master)="row">
                  <span>{{row.item.gm_first_name}} {{row.item.gm_last_name}}</span>
                </template>
                <template v-slot:cell(actions)="row">
                  <el-dropdown trigger="click">
                    <base-button
                      simple
                      type="primary"
                      link
                    > <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                    </base-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <router-link v-if="row.item.status === 'in_progress'"
                                     class="dropdown-item mt-1 mb-2" :to="'/checklists/active?run=' + row.item.id">
                          <i class="tim-icons icon-refresh-02 text-default mr-2"></i> Resume run
                        </router-link>
                        <router-link v-if="row.item.status !== 'in_progress'" class="dropdown-item mt-1 mb-2" :to="'/checklists/history/instance?id=' + row.item.id">
                          <i class="tim-icons icon-zoom-split text-default mr-2"></i> View details
                        </router-link>
                        <a v-if="hasPermissions('checklist_history_update') && row.item.status !== 'in_progress'"
                           class="dropdown-item mt-1 mb-2" href="#" @click.prevent="editItem(row.item)">
                          <i class="tim-icons icon-time-alarm text-default mr-2"></i> Adjust times
                        </a>
                        <hr v-if="hasPermissions('checklist_history_delete')">
                        <a v-if="hasPermissions('checklist_history_delete')"
                           class="dropdown-item" href="#" @click.prevent="deleteChecklistPrompt(row.item)">
                          <i class="tim-icons text-default icon-trash-simple mr-2"></i> Delete run
                        </a>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </template>
              </b-table>
              <b-pagination v-on:input="updateCompletedChecklist()"
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            :limit="3"
              ></b-pagination>
            </div>
          </div>
          </div>

          <!--Mobile-->
          <div class="mt-3 d-block d-sm-none">
          <div class="mt-3" v-if="noDataAvailable === true">
            <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <h4 class="mt-3">No data available.</h4>
            </div>
          </div>
          <div v-if="noDataAvailable === false" class="row">
            <div class="col-md-12 col-lg-12 col-sm-12 col-12">
              <b-table  :items="completedResets"
                        :current-page="currentPage"
                        :fields="mobile_fields">
                <template #head(reset)="data">
                  <span> </span>
                </template>

                <template v-slot:cell(game_master)="row">
                  <span>{{row.item.gm_first_name}} {{row.item.gm_last_name}}</span>
                </template>
                <template v-slot:cell(completed_by)="row">
                  <span>{{row.item.r_first_name}} {{row.item.r_last_name}}</span>
                </template>

                <template v-slot:cell(reset)="row">
                  <div class="row">
                    <div class="col">
                      <router-link :to="'/checklists/history/instance?id=' + row.item.id" class="checklist-event-link" style="font-size: medium">
                        {{row.item.checklist_name}}
                        <i class="tim-icons icon-minimal-right ml-1" style="font-size: 10px"></i>
                      </router-link>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col">
                      <span class="cr-pill" :class="statusClass(row.item.status)">{{ statusLabel(row.item.status) }}</span>
                      <span class="cr-pill cr-pill--muted ml-1">{{ row.item.steps_completed }} / {{ row.item.steps_total }} steps</span>
                      <span v-if="row.item.issues_count > 0" class="cr-pill cr-pill--danger ml-1">{{ row.item.issues_count }} issue{{ row.item.issues_count > 1 ? 's' : '' }}</span>
                    </div>
                  </div>
                  <div class="row" v-if="row.item.tags && row.item.tags.length">
                    <div class="col">
                      <div class="tags-row mt-1">
                        <span v-for="tag in row.item.tags" :key="'MT' + row.item.id + '-' + tag.id" class="tag-chip text-white"
                              :class="{ 'tag-chip--archived': tag.is_archived }" :style="tagChipStyle(tag.color)">{{ tag.name }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col">
                      <span class="small">Started by {{row.item.r_first_name}} {{row.item.r_last_name}} on {{getLocalDate(row.item.reset_start_time)}}, {{getLocalTime(row.item.reset_start_time)}}</span>
                    </div>
                  </div>
                  <div class="row" v-if="row.item.status !== 'in_progress'">
                    <div class="col">
                      <span class="small">Completed by {{row.item.cb_first_name}} {{row.item.cb_last_name}} on {{getLocalDate(row.item.timestamp)}}, {{getLocalTime(row.item.timestamp)}}</span>
                    </div>
                  </div>
                  <div class="row" v-if="row.item.status !== 'in_progress'">
                    <div class="col">
                      <span class="small">Completion time: {{getCompletionTime(row.item.reset_start_time, row.item.timestamp)}}</span>
                    </div>
                  </div>

                  <div v-if="row.item.game_id" class="ch-event mt-2">
                    <span class="cr-pill">{{ row.item.game_name }}</span>
                    <template v-if="row.item.game_instance_id">
                      <a :href="'/games/history/instance?id=' + row.item.game_instance_id" target="_blank" class="checklist-event-link ch-event__session">
                        {{ getBookingDate(row.item.booking_date) }}, {{ getTime(row.item.start_time) }}
                        <i class="tim-icons icon-minimal-right ml-1" style="font-size: 10px"></i>
                      </a>
                      <span class="ch-event__host">Hosted by {{ row.item.gm_first_name }} {{ row.item.gm_last_name }}</span>
                    </template>
                  </div>
                </template>
              </b-table>
              <b-pagination v-on:input="updateCompletedChecklist()"
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
              ></b-pagination>
            </div>
          </div>
          </div>
        </card>
      </div>
    </div>

<!--    Edit drawer-->
    <base-drawer
      :show="modals.edit_checklist"
      title="Adjust run times"
      @close="modals.edit_checklist = false"
    >
      <drawer-section v-if="selectedChecklistItem && selectedChecklistItem.game_id" title="Event" icon="icon-shape-star">
        <template #header-actions>
          <badge v-if="selectedChecklistItem.game_name" type="primary">{{ selectedChecklistItem.game_name }}</badge>
        </template>
        <base-input label="Linked event session">
          <el-select
            clearable
            class="select-primary"
            style="width: 100%"
            name="game_instance"
            placeholder="No event linked"
            v-model="selectedItem.game_instance_id"
          >
            <el-option
              v-for="(g, i) in gameInstanceOptions"
              class="select-primary"
              :label="getBookingDate(g.booking_date) + ', ' + getTime(g.start_time)"
              :value="g.id"
              :key="'GI' + i"
            ></el-option>
          </el-select>
        </base-input>
        <info-hint v-if="eventMismatch" variant="warning" icon="icon-alert-circle-exc">
          This session is for {{ selectedChecklistItem.game_name }}, but the checklist is now linked to
          {{ checklistCurrentGameName || 'a different event' }}. Relinking will pick from {{ checklistCurrentGameName || 'the current event' }}'s sessions.
        </info-hint>
        <info-hint>
          Clear the selection to unlink this event from the checklist, or pick a different session to relink.
        </info-hint>
      </drawer-section>

      <drawer-section title="Timing" icon="icon-time-alarm">
        <div class="row">
          <div class="col-12 col-sm-6">
            <base-input label="Start date">
              <el-date-picker :editable="false"
                style="width: 100%"
                v-model="selectedItem.start_date"
                :format="day_of_week + '' + date_format_cal"
                :picker-options="{ firstDayOfWeek: first_day_of_week }"
              ></el-date-picker>
            </base-input>
          </div>
          <div class="col-12 col-sm-6">
            <base-input label="Start time">
              <el-time-picker
                style="width: 100%"
                v-model="selectedItem.start_time"
                :format="picker_time_format"
                :placeholder="picker_time_format === 'HH:mm' ? 'HH:mm' : 'hh:mm AM'"
              ></el-time-picker>
            </base-input>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-12 col-sm-6">
            <base-input label="Completion date">
              <el-date-picker :editable="false"
                style="width: 100%"
                v-model="selectedItem.end_date"
                :format="day_of_week + '' + date_format_cal"
                :picker-options="{ firstDayOfWeek: first_day_of_week }"
              ></el-date-picker>
            </base-input>
          </div>
          <div class="col-12 col-sm-6">
            <base-input label="Completion time">
              <el-time-picker
                style="width: 100%"
                v-model="selectedItem.end_time"
                :format="picker_time_format"
                :placeholder="picker_time_format === 'HH:mm' ? 'HH:mm' : 'hh:mm AM'"
              ></el-time-picker>
            </base-input>
          </div>
        </div>
      </drawer-section>

      <template #footer>
        <div class="d-flex justify-content-end" style="gap: 8px;">
          <base-button type="secondary" size="sm" @click="modals.edit_checklist = false">Cancel</base-button>
          <base-button type="primary" size="sm" :disabled="loading" @click="saveItem()">Save</base-button>
        </div>
      </template>
    </base-drawer>
  <base-alert-dialog
    :show="alert.show"
    :title="alert.title"
    :message="alert.message"
    :variant="alert.variant"
    @confirm="onAlertConfirm()"
    @cancel="alert.show = false"
  />
  <base-alert-dialog
    :show="confirm.show"
    title="Delete this run?"
    message="The run and everything recorded on it will be permanently deleted."
    variant="danger"
    show-cancel
    confirm-text="Delete"
    confirm-variant="danger"
    @confirm="onConfirmDelete()"
    @cancel="confirm.show = false"
  />
  </div>
</template>

<script>
import {
  Select,
  Option,
  DatePicker,
  TimePicker,
  Dropdown,
  DropdownMenu
} from 'element-ui';
import { Auth } from 'aws-amplify'
import {BTable, BPagination} from 'bootstrap-vue'
import {API_LOCATION} from "@/api-config";
import Badge from "@/components/Badge"
const axios = require('axios')
import {dayOfWeek, formattedDate, time} from "@/plugins/dateFormatter";
import {formatGroupDate, formatGroupTime} from "@/plugins/dates";
import {tagChipStyle} from "@/util/tagChip";
import moment from "moment-timezone";

export default {
  name: "ChecklistHistory",
  components: {
    [DatePicker.name]: DatePicker,
    [Select.name]: Select,
    [Option.name]: Option,
    [TimePicker.name]: TimePicker,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    BTable,
    BPagination,
    Badge
  },
  data() {
    return {
      alert: { show: false, title: '', message: '', variant: 'warning', redirect: '' },
      confirm: { show: false, item: null },
      selectedChecklistItem: '',
      selectedItem: {
        start_date: '',
        start_time: '',
        end_date: '',
        end_time: '',
        game_instance_id: null
      },
      gameInstanceOptions: [],
      eventMismatch: false,
      checklistCurrentGameName: '',
      loading: false,
      refreshing: false,
      load: false,
      didInitialFetch: false,
      noDataAvailable: false,
      filterValues: this.buildInitialFilterValues(),
      completedResets: [],
      perPage: 10,
      currentPage: 1,
      totalRows: 0,
      perPageOptions: [5, 10, 25, 50],
      fields: ['checklist', { key: 'game', label: 'Event' }, 'started_at', 'completed_at', 'completion_time'],
      mobile_fields: ['reset'],
      gameOptions: [],
      checklistOptions: [],
      tagCatalog: [],
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      first_day_of_week: JSON.parse(localStorage.getItem('group')).first_day_of_week || 7,
      date_format_cal: '',
      day_of_week: '',
      picker_time_format: 'hh:mm A',
      modals: {
        edit_checklist: false
      }
    };
  },
  computed: {
    filterSchema() {
      return [
        {key: 'checklist', type: 'select', label: 'Checklist', primary: true,
          options: this.checklistOptions.map(c => ({label: c.archived === 1 ? c.name + ' (archived)' : c.name, value: c.id}))},
        {key: 'status', type: 'select', label: 'Status', primary: true,
          options: [
            {label: 'In progress', value: 'in_progress'},
            {label: 'Completed', value: 'completed'},
            {label: 'Awaiting approval', value: 'awaiting_approval'},
            {label: 'Approved', value: 'approved'},
            {label: 'Rejected', value: 'rejected'},
          ]},
        {key: 'date', type: 'daterange', label: 'Completion time', primary: true},
        {key: 'event', type: 'select', label: 'Event', primary: true,
          options: this.gameOptions.map(g => ({label: g.name, value: g.id}))},
        {key: 'tags', type: 'multiselect', label: 'Tags',
          options: this.tagCatalog.filter(t => !t.is_archived).map(t => ({label: t.name, value: t.id}))},
        {key: 'game_master', type: 'text', label: 'Event host', placeholder: 'Name', operatorLabel: 'contains'},
        {key: 'resetter', type: 'text', label: 'Staff', placeholder: 'Started or completed by', operatorLabel: 'contains'},
      ]
    }
  },
  methods: {
    showAlert(title, message, variant, redirect){
      this.alert.title = title
      this.alert.message = message
      this.alert.variant = variant || 'warning'
      this.alert.redirect = redirect || ''
      this.alert.show = true
    },
    onAlertConfirm(){
      this.alert.show = false
      if(this.alert.redirect){
        this.$router.push(this.alert.redirect)
      }
    },
    buildInitialFilterValues() {
      const q = this.$route.query || {}
      return {
        checklist: q.checklist ? Number(q.checklist) : '',
        event: q.game ? Number(q.game) : '',
        game_master: q.game_master || '',
        resetter: q.resetter || '',
        status: q.status || '',
        tags: q.tags ? String(q.tags).split(',').map(Number).filter(n => Number.isFinite(n) && n > 0) : [],
        date: { mode: 'range', days: '', from: q.from || '', to: q.to || '' },
      }
    },
    computeDateRange() {
      const d = this.filterValues.date || {}
      let to = ''
      let from = ''
      if (d.mode === 'days' && d.days !== '' && d.days != null) {
        let today = new Date()
        let dt = new Date()
        dt.setDate(dt.getDate() - Number(d.days))
        let userStartDate = dt.getFullYear() + "-" + ('0' + (dt.getMonth() + 1)).slice(-2) + "-" + ('0' + dt.getDate()).slice(-2)
        let userEndDate = today.getFullYear() + "-" + ('0' + (today.getMonth() + 1)).slice(-2) + "-" + ('0' + today.getDate()).slice(-2)
        from = new Date(`${userStartDate}T00:00:00`).toISOString()
        to = new Date(`${userEndDate}T23:59:59`).toISOString()
      } else if (d.mode === 'range' && d.from && d.to) {
        from = new Date(`${d.from}T00:00:00`).toISOString()
        to = new Date(`${d.to}T23:59:59`).toISOString()
      }
      return { to, from }
    },
    onFiltersChanged(values) {
      this.filterValues = { ...this.filterValues, ...values, date: { ...values.date } }
      // FilterBar's first change is the saved-views resolution; keep the page the
      // URL restored. Later changes reset to page 1 before computing the offset.
      if (!this.didInitialFetch) {
        this.didInitialFetch = true
        this.updateCompletedChecklist(null, true)
      } else {
        this.updateCompletedChecklist('filter', true)
      }
    },
    onPerPageChange(n) {
      this.perPage = n
      this.currentPage = 1
      this.updateCompletedChecklist('filter', true)
    },
    deleteChecklistPrompt(item){
      this.confirm.item = item
      this.confirm.show = true
    },
    onConfirmDelete(){
      this.confirm.show = false
      if(this.confirm.item){
        this.deleteChecklist(this.confirm.item)
      }
    },
    async deleteChecklist(item){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/checklists/instances/' + item.id
      this.loading = true
      this.axios.delete(url, config)
        .then(response => {
          this.$notify({ type: 'success', message: 'Run deleted', icon: 'tim-icons icon-check-2' })
          this.updateCompletedChecklist()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.showAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', '/')
          }
          else if(err.response.status == 500){
            this.showAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', '/')
          }
          else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async saveItem(){
      const tz = JSON.parse(localStorage.getItem('group')).tz_name
      const toUtc = (datePart, timePart) => {
        const d = new Date(datePart)
        const t = new Date(timePart)
        const y = d.getFullYear()
        const mo = String(d.getMonth() + 1).padStart(2, '0')
        const da = String(d.getDate()).padStart(2, '0')
        const hh = String(t.getHours()).padStart(2, '0')
        const mi = String(t.getMinutes()).padStart(2, '0')
        const localStr = `${y}-${mo}-${da} ${hh}:${mi}:00`
        return (tz ? moment.tz(localStr, tz) : moment(localStr)).toDate()
      }
      const start_time = toUtc(this.selectedItem.start_date, this.selectedItem.start_time)
      const end_time = toUtc(this.selectedItem.end_date, this.selectedItem.end_time)
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/checklists/instances'
      let data = {
        id: this.selectedChecklistItem.id,
        action: 'edit_timing',
        start_time: start_time,
        end_time: end_time,
        game_instance_id: this.selectedItem.game_instance_id || null
      }

      this.loading = true
      this.axios.put(url, data, config)
        .then(response => {
          this.modals.edit_checklist = false
          this.$notify({ type: 'success', message: 'Run updated', icon: 'tim-icons icon-check-2' })
          this.updateCompletedChecklist()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.showAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', '/')
          }
          else if(err.response.status == 500){
            this.showAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', '/')
          }
          else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    editItem(item){
      this.selectedChecklistItem = item
      const tz = JSON.parse(localStorage.getItem('group')).tz_name
      const toWallClock = (utc) => {
        if(!utc) return null
        const m = tz ? moment.utc(utc).tz(tz) : moment(utc)
        return new Date(m.year(), m.month(), m.date(), m.hours(), m.minutes())
      }
      const startWall = toWallClock(item.reset_start_time)
      const endWall = toWallClock(item.timestamp)
      this.selectedItem.start_date = startWall
      this.selectedItem.start_time = startWall
      this.selectedItem.end_date = endWall
      this.selectedItem.end_time = endWall
      this.selectedItem.game_instance_id = item.game_instance_id || null
      this.gameInstanceOptions = []
      this.eventMismatch = false
      this.checklistCurrentGameName = ''
      this.modals.edit_checklist = true
      // Only checklists configured with a linked event can have/relink a session.
      if (item.game_id) {
        this.loadGameInstanceOptions(item)
      }
    },
    async loadGameInstanceOptions(item){
      // Candidate sessions to relink to are the unlinked game instances for
      // this checklist's event (the /checklists/start payload already scopes
      // them to c.game_id and excludes already-linked instances). Prepend the
      // currently-linked session so it stays selectable.
      const options = []
      if(item.game_instance_id){
        options.push({
          id: item.game_instance_id,
          booking_date: item.booking_date,
          start_time: item.start_time
        })
      }
      // Seed the currently-linked session synchronously (before the await) so the
      // select renders its date/time label immediately instead of flashing the raw id.
      this.gameInstanceOptions = options.slice()
      try {
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/checklists/start'
        let response = await this.axios.get(url, config)
        const checklist = (response.data || []).find(c => c.id === item.checklist_id)
        // Flag when the linked session's event no longer matches the checklist's
        // current event (the checklist's game_id was changed after this run).
        if (item.game_instance_id && checklist && checklist.game_id && Number(checklist.game_id) !== Number(item.game_id)) {
          this.eventMismatch = true
          const g = this.gameOptions.find(o => Number(o.id) === Number(checklist.game_id))
          this.checklistCurrentGameName = g ? g.name : ''
        }
        const available = checklist && checklist.game_instances ? JSON.parse(checklist.game_instances) : []
        for(const gi of available){
          if(gi && gi.id && !options.some(o => o.id === gi.id)){
            options.push({id: gi.id, booking_date: gi.booking_date, start_time: gi.start_time})
          }
        }
      } catch (err) {
        console.log(err)
      }
      this.gameInstanceOptions = options
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    tagChipStyle,
    tagsParam(){
      const v = this.filterValues.tags
      return Array.isArray(v) && v.length ? v.join(',') : ''
    },
    async getTagCatalog(){
      try {
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        const response = await this.axios.get(API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/tags', config)
        this.tagCatalog = Array.isArray(response.data) ? response.data : []
      } catch (e) {
        this.tagCatalog = []
      }
    },
    statusLabel(status){
      const labels = { in_progress: 'In progress', completed: 'Completed', awaiting_approval: 'Awaiting approval', approved: 'Approved', rejected: 'Rejected' }
      return labels[status] || 'Completed'
    },
    statusClass(status){
      const classes = { in_progress: 'cr-pill--info', completed: 'cr-pill--success', awaiting_approval: 'cr-pill--warning', approved: '', rejected: 'cr-pill--danger' }
      return classes[status] === undefined ? 'cr-pill--success' : classes[status]
    },
    getCompletionTime(start, end){
      if(!start || ! end){
        return '-'
      }
      let s = new Date(start)
      let e = new Date(end)
      let diff = Math.abs(e - s)
      let mins = Math.ceil(diff / (1000 * 60));
      if(mins === 1){
        return mins + ' minute'
      }
      return mins + ' minutes'
    },
    getDayOfWeek(d){
      if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
        return dayOfWeek(d) + ', '
      }
      return ''
    },
    async getChecklists(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/checklists'
      this.axios.get(url, config)
        .then(response => {
          // Only loads the checklist filter dropdown options. The results table
          // is driven by FilterBar's initial @change (see onFiltersChanged), so
          // don't fetch it here or the first page would load twice.
          this.checklistOptions = response.data
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.showAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', '/')
          }
          else if(err.response.status == 500){
            this.showAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', '/')
          }
          else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    getBookingDate(d){
      return formattedDate(this.date_format, d)
    },
    getTime(d){
      return time(this.time_format, d)
    },
    getLocalDate(d){
      return formatGroupDate(d)
    },
    getLocalTime(d){
      return formatGroupTime(d)
    },
    async updateCompletedChecklist(source, silent){
      if(source === 'filter'){
        this.currentPage = 1
      }
      const { to, from } = this.computeDateRange()
      // Guard against NaN/0/negative page (stale URL, out-of-range restore) which
      // would send a negative offset and 502 the backend. Clamp offset to >= 0.
      let count = Number(this.perPage) || 10
      let page = Number(this.currentPage) || 1
      let offset = Math.max(0, parseInt((page * count) - count))
      await this.getCompletedResets(count, offset, this.filterValues.event, this.filterValues.game_master, this.filterValues.resetter, to, from, this.filterValues.checklist, silent, this.filterValues.status, this.tagsParam())
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async getGames () {
      this.loading = true
      var token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/games'
      axios.get(url, config)
        .then(response => {
          this.loading = false
          this.gameOptions = response.data
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.showAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', '/')
          }
          else if(err.response.status == 500){
            this.showAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', '/')
          }
          else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async getCompletedResets (count, offset, game, game_master, resetter, to, from, checklist, silent, status, tags) {

      if(game_master === undefined){
        game_master = ''
      }
      if(resetter === undefined){
        resetter = ''
      }
      if(game === undefined){
        game = ''
      }
      if(checklist === undefined){
        checklist = ''
      }
      if(to === undefined){
        to = ''
      }
      if(from === undefined){
        from = ''
      }

      const token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/checklists/instances?count=' + count + "&offset=" + offset + "&game=" + encodeURIComponent(game)
        + "&game_master=" + encodeURIComponent(game_master) + '&resetter=' + encodeURIComponent(resetter) + "&to=" + to + "&from=" + from + "&checklist=" + encodeURIComponent(checklist)
        + "&status=" + encodeURIComponent(status || '') + "&tags=" + encodeURIComponent(tags || '')
      if (silent) { this.refreshing = true } else { this.load = false }
      await this.getGameInstanceURL(url, config)
      if (silent) { this.refreshing = false }
      // silent controls the spinner style only; the table must render either way
      this.load = true
    },
    escapeCSVValue(value) {
      if (value === null || value === undefined) {
        return ''
      }
      value = String(value).replace(/"/g, '""')
      if (value.includes(',') || value.includes('\n') || value.includes('"')) {
        value = `"${value}"`
      }
      return value
    },
    buildInstancesUrl(count, offset) {
      const { to, from } = this.computeDateRange()
      const f = this.filterValues
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      return API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/checklists/instances?count=' + count + '&offset=' + offset
        + '&game=' + encodeURIComponent(f.event || '') + '&game_master=' + encodeURIComponent(f.game_master || '')
        + '&resetter=' + encodeURIComponent(f.resetter || '') + '&to=' + to + '&from=' + from
        + '&checklist=' + encodeURIComponent(f.checklist || '') + '&status=' + encodeURIComponent(f.status || '') + '&tags=' + encodeURIComponent(this.tagsParam())
    },
    // Pages through every row matching the active filters (the table only holds
    // the current page). Sequential so one 401 stops the loop instead of fanning out.
    async getAllCompletedResets(config) {
      const count = 1000
      let offset = 0
      let rows = []
      let total = this.totalRows
      while (offset < total) {
        const response = await axios.get(this.buildInstancesUrl(count, offset), config)
        const page = response.data.checklist_instances || []
        rows = rows.concat(page)
        total = Number(response.data.totalRows) || total
        if (page.length === 0) {
          break
        }
        offset += count
      }
      return rows
    },
    async downloadCSV() {
      try {
        this.loading = true
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const rows = await this.getAllCompletedResets(config)

        let csv = 'Checklist,Event,Event date,Event time,Event host,Started by,Completed by,Status,Steps done,Steps total,Issues,Tags,Started at,Completed at,Completion time (minutes)\n'
        rows.forEach(row => {
          csv += [
            this.escapeCSVValue(row.checklist_name),
            this.escapeCSVValue(row.game_id ? row.game_name : ''),
            this.escapeCSVValue(row.game_instance_id ? this.getBookingDate(row.booking_date) : ''),
            this.escapeCSVValue(row.game_instance_id ? this.getTime(row.start_time) : ''),
            this.escapeCSVValue(row.game_instance_id ? [row.gm_first_name, row.gm_last_name].filter(Boolean).join(' ') : ''),
            this.escapeCSVValue([row.r_first_name, row.r_last_name].filter(Boolean).join(' ')),
            this.escapeCSVValue(row.status === 'in_progress' ? '' : [row.cb_first_name, row.cb_last_name].filter(Boolean).join(' ')),
            this.escapeCSVValue(this.statusLabel(row.status)),
            this.escapeCSVValue(row.steps_completed),
            this.escapeCSVValue(row.steps_total),
            this.escapeCSVValue(row.issues_count),
            this.escapeCSVValue((row.tags || []).map(t => t.name).join('; ')),
            this.escapeCSVValue(row.reset_start_time ? this.getLocalDate(row.reset_start_time) + ' ' + this.getLocalTime(row.reset_start_time) : ''),
            this.escapeCSVValue(row.timestamp ? this.getLocalDate(row.timestamp) + ' ' + this.getLocalTime(row.timestamp) : ''),
            this.escapeCSVValue(this.getCompletionMinutes(row.reset_start_time, row.timestamp))
          ].join(',') + '\n'
        })

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', 'checklist-history.csv')
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (err) {
        console.log(err)
        this.$notify({
          message: 'Something went wrong while exporting the checklist history.',
          timeout: 4000,
          icon: 'tim-icons icon-alert-circle-exc',
          horizontalAlign: 'right',
          verticalAlign: 'top',
          type: 'danger'
        })
      } finally {
        this.loading = false
      }
    },
    getCompletionMinutes(start, end) {
      if (!start || !end) {
        return ''
      }
      const diff = Math.abs(new Date(end) - new Date(start))
      return Math.ceil(diff / (1000 * 60))
    },
    getGameInstanceURL(url, config){
      axios.get(url, config)
        .then(response => {
          if(this.completedResets.length <=0){
            this.currentPage = Number(this.$route.query.page)
          }
          this.completedResets = response.data.checklist_instances
          if(this.completedResets.length <= 0 ){
            this.noDataAvailable = true
          } else {
            this.noDataAvailable = false
          }
          this.totalRows = response.data.totalRows
          this.addQueryParameters()

        })
        .catch(err => {
          console.log(err)
          if(err.response.status == 401){
            this.showAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', '/')
          }
          else if(err.response.status == 500){
            this.showAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', '/')
          }
          else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })


    },
    addQueryParameters() {
      this.$nextTick(() => {
        this.currentPage = this.$route.query.page
      });
      const newQuery = {
        ...this.$route.query,
        count: this.perPage,
        page: this.currentPage,
        resetter: this.filterValues.resetter,
        game_master: this.filterValues.game_master,
        checklist: this.filterValues.checklist,
        game: this.filterValues.event,
        to: this.filterValues.date.to,
        from: this.filterValues.date.from
      }
      this.$router.replace({query: newQuery}).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
  },
  created() {
    this._onEscKey = (e) => {
      if (e.key === 'Escape' && this.modals.edit_checklist) {
        this.modals.edit_checklist = false
      }
    }
    document.addEventListener('keydown', this._onEscKey)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._onEscKey)
  },
  mounted() {
    this.fields = ['checklist', { key: 'game', label: 'Event' }, 'completed_by', 'started_at', 'completed_at', 'completion_time', 'actions']
    this.date_format_cal = this.date_format
    this.date_format_cal =  this.date_format_cal.replaceAll('Y', 'y')
    this.date_format_cal = this.date_format_cal.replaceAll('D', 'd')
    if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
      this.day_of_week = 'ddd, '
    }
    if (this.time_format === 'HH:mm') {
      this.picker_time_format = 'HH:mm'
    }
    this.perPage = Number(this.$route.query.count) || 10
    this.currentPage = Number(this.$route.query.page) || 1

    // FilterBar emits its initial @change after mount, which drives the first
    // results fetch via onFiltersChanged. These two calls only load the filter
    // dropdown options (checklists, games), not the results table.
    this.loading = true
    this.getChecklists()
    this.getTagCatalog()
    this.getGames()
  }
};
</script>
<style scoped>
.ch-event {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}
.ch-event .cr-pill {
  margin-bottom: 4px;
}
.ch-event__session {
  font-size: 0.8rem;
}
.ch-event__host {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}
.white-content .ch-event__host {
  color: rgba(52, 70, 117, 0.65);
}
.checklist-event-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.15s ease;
}
.checklist-event-link:hover {
  color: #e14eca;
}
.checklist-event-link:hover .tim-icons {
  color: #e14eca !important;
}
.table-content {
  overflow-x: scroll;
}

.el-loading-spinner .path {
  stroke: #6aff8d !important;
}

.el-loading-mask {
  background: rgba(0, 0, 0, 0.7) !important;
}
</style>
